/**
 * Phase 105-Agent-C — NearestTaskBanner.
 *
 * Top-of-page banner rendered above the map on /dashboard/agent/tasks.
 * Polls `/api/agent/nearest-tasks` every 30 sec while the agent has
 * tasks AND a known location. Tapping the banner bubbles the chosen
 * task's tracking_id up so AgentTasks can select it in the side sheet.
 *
 * Renders nothing when the agent has zero tasks, OR when the agent has
 * no known location yet (the hint surfaces in the AgentTasks empty
 * state instead).
 */
import { useEffect, useState } from "react";
import { Compass, ArrowRight, Loader2 } from "lucide-react";
import api from "../../services/api";

const POLL_MS = 30_000;

export default function NearestTaskBanner({ onSelect, hasLocation = true }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [computedFrom, setComputedFrom] = useState(null);
  const [hint, setHint] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/agent/nearest-tasks");
        if (cancelled) return;
        setRows(data?.rows || []);
        setComputedFrom(data?.computed_from || null);
        setHint(data?.hint || null);
      } catch {
        /* stays in previous state; banner falls back to hidden if empty */
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    tick();
    const t = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Only show banner when we have rows AND a computed location AND there's
  // a meaningful nearest task to highlight.
  const nearest = rows.find((r) => r.distance_km != null);
  if (loading) {
    return (
      <div className="bg-white border-b border-slate-200 px-4 py-2 text-xs text-slate-500 flex items-center gap-2" data-testid="nearest-task-loading">
        <Loader2 className="h-3 w-3 animate-spin" /> Calculating nearest pickup…
      </div>
    );
  }
  if (!nearest || !hasLocation || !computedFrom) {
    // When the hint is set (no location), it's surfaced elsewhere — keep
    // this component silent so we don't double-warn.
    if (hint) return null;
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => onSelect && onSelect(nearest.tracking_id)}
      className="w-full text-left bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-200 px-4 py-3 flex items-center gap-3 hover:from-orange-100 hover:to-amber-100 transition group"
      data-testid="nearest-task-banner"
    >
      <span className="w-9 h-9 rounded-xl bg-[#FF6B00] text-white flex items-center justify-center shrink-0">
        <Compass className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider font-bold text-orange-700">Nearest pickup</p>
        <p className="text-sm font-semibold text-slate-900 truncate">
          <span className="font-mono">{nearest.tracking_id}</span>
          <span className="mx-2 text-slate-300">·</span>
          {nearest.distance_km} km
          <span className="mx-2 text-slate-300">·</span>
          {nearest.eta_minutes} min ETA
        </p>
        {nearest.pickup_address && (
          <p className="text-xs text-slate-600 truncate">{nearest.pickup_address}</p>
        )}
      </div>
      <ArrowRight className="h-4 w-4 text-orange-600 shrink-0 group-hover:translate-x-1 transition-transform" />
    </button>
  );
}
